import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Cliente } from '../../../core/models/clientes/cliente';
import { Configuracao } from '../../../core/models/configuracoes/configuracao';
import { PrazoMedio } from '../../../core/models/prazos-medio/prazo-medio';
import { PrazoMedioDesconto } from '../../../core/models/prazos-medio/prazo-medio-desconto';
import { PrazoPagamento } from '../../../core/models/prazos-pagamento/prazo-pagamento';

/**
 * Toolbar / cabeçalho do modal Adicionar Itens
 * (pedido-adicionar-itens-modal.component.html:7-92): prazo de pagamento, prazo médio,
 * desconto de prazo médio, saldo de conta corrente, limite de crédito / crédito
 * financeiro do cliente, pontuação total e os atalhos de ação (IA Napis / Sugerir
 * Produtos / Cancelar / Salvar).
 *
 * `<app-prazo-pagamento-select>` (componente compartilhado já existente em
 * `components/shared/prazo-pagamento-select`) é usado aqui tal como estava no bloco
 * original — seus próprios inputs/outputs não mudaram.
 *
 * Os estilos/markup do overlay e painel de sugestões de IA (`.ia-overlay`,
 * `.ia-produtos-painel`, `.ia-painel-*`, `.ia-sugestao-*` etc. — html:94+) NÃO
 * pertencem a este componente: ficam no pai / são escopo da Task 7. Apenas os dois
 * botões de atalho (`.ia-atalhos-itens`) que abrem esse painel vivem aqui.
 *
 * `pontuacaoTotal`: verificado contra o bloco real — a "Pontuação (Total)" exibida
 * (html:70-75 original) interpola `valorTotalPontuacaoProdutosAdicionados` (um
 * `@Input() number` do próprio componente pai, não um getter), não um campo chamado
 * literalmente `pontuacaoTotal`. O pai passa esse valor via `[pontuacaoTotal]`.
 *
 * `menuMobileExpandido`: não listado no brief original ("Inputs" da seção
 * Interfaces), mas necessário — controla a classe `adicionar_itens_menu_mobile_fechado`
 * no `ngClass` da raiz deste bloco (html:7 original) e é alternado pelo botão
 * "Mostrar/Ocultar menu" que permanece no pai (html:2-6, fora do range 7-92, não
 * migrado para este componente).
 *
 * `toolbarRoot` (`@ViewChild`): o pai tinha `@ViewChild("adicionarItensDados")
 * adicionarItensDados: ElementRef` apontando para a raiz deste bloco, usado em
 * `recalcularViewportHeight()` (ts:925-934) para medir `offsetHeight` e calcular a
 * altura do virtual scroll. Ao mover a `div` para este componente, aquele
 * `@ViewChild` ficaria permanentemente `undefined` (mesma classe de bug já corrigida
 * no "Fix round 2 (Task 5 review)" documentado no componente `catalogo`, para
 * `#adicionarItensProdutos`/`viewport`). Exposto aqui como `ElementRef` público para
 * o pai reconectar via `@ViewChild(PedidoItensToolbarComponent)` + um getter privado
 * com o MESMO nome/tipo do campo original, sem duplicar nenhuma lógica de
 * medição/resize (que continua 100% no pai, inalterada).
 */
@Component({
  selector: 'app-pedido-itens-toolbar',
  standalone: false,
  templateUrl: './pedido-itens-toolbar.component.html',
  styleUrls: ['./pedido-itens-toolbar.component.scss']
})
export class PedidoItensToolbarComponent {
  @Input() configuracao: Configuracao = null;
  @Input() clienteSelecionado: Cliente = null;

  @Input() prazosPagamentoIniciais: PrazoPagamento[] = [];
  @Input() prazosPagamentoEspeciais: PrazoPagamento[] = [];
  @Input() prazoPagamentoSelecionado: PrazoPagamento = null;
  @Input() prazoPagamentoInvalido = false;
  @Input() prazoMedio: PrazoMedio = null;
  @Input() prazoMedioDesconto: PrazoMedioDesconto = null;

  @Input() contaCorrenteSaldoDisponivel = 0;
  @Input() saldoGeradoValorParcial = 0;

  @Input() exibirPontuacao = false;
  @Input() pontuacaoTotal = 0;

  @Input() iaCarregando = false;
  @Input() calculando = false;

  @Input() menuMobileExpandido = false;

  @Output() prazoAlterado = new EventEmitter<{ id_prazo_pagamento: string; cabecalho: boolean; event?: Event }>();
  @Output() prazoCancelado = new EventEmitter<Event | undefined>();
  @Output() iaPainelAlternado = new EventEmitter<void>();
  @Output() produtosSugeridos = new EventEmitter<void>();
  @Output() salvarClicado = new EventEmitter<void>();
  @Output() cancelarClicado = new EventEmitter<void>();

  /** Ver comentário da classe — reconexão do `@ViewChild("adicionarItensDados")` do pai. */
  @ViewChild('toolbarRoot') toolbarRoot: ElementRef;
}
